import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import { kebabCase } from 'lodash'

import { getUnprocessableErrors } from 'utils/error'
import { Button } from 'components/v2/Button'
import Card from '@10xLabs/react-web/components/Card'
import { Input } from 'components/Input'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Select } from 'components/v2/Select'
import { InputTags } from 'components/v2/InputTags'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { SelectState } from 'features/Cities/components/SelectState'
import { SelectTimezone } from 'features/Cities/components/SelectTimezone'
import { newCitySchema } from './schema'
import { COUNTRIES } from 'utils/countries'
import { getStateNameByCountryAndStateCode } from 'utils/states'

const messages = defineMessages({
  namePlaceholder: {
    id: 'cities.new.form.name.placeholder',
    defaultMessage: 'Name'
  },
  paramPlaceholder: {
    id: 'cities.new.form.param.placeholder',
    defaultMessage: 'Alias for the route url'
  },
  codePlaceholder: {
    id: 'places.new.form.code.placeholder',
    defaultMessage: 'Code for segments autocomplete'
  },
  selectCountryCode: {
    id: 'cities.new.form.countryCode.select',
    defaultMessage: 'Choose country'
  },
  tagsPlaceholder: {
    id: 'cities.new.form.tags.placeholder',
    defaultMessage: 'Press Enter to add tag'
  }
})

const NewCityForm = (props: TSFixMe): TSFixMe => {
  const { values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue, error, loading, isEdit } =
    props

  const intl = useIntl()

  const handleTagsChange = (values: TSFixMe): TSFixMe => {
    setFieldValue('tags', values)
  }

  const handleCountryChange = ({ target: { value } }: TSFixMe): TSFixMe => {
    setFieldValue('countryCode', value)

    // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
    setFieldValue('param', getParamValue(values.name))
  }

  const handleNameChange = ({ target: { value } }: TSFixMe): TSFixMe => {
    setFieldValue('name', value)

    setFieldValue('param', getParamValue(value, values.stateCode))
  }

  const handleStateChange = ({ target: { value } }: TSFixMe): TSFixMe => {
    setFieldValue('stateCode', value)

    setFieldValue('param', getParamValue(values.name, value))
  }

  const getParamValue = (name: TSFixMe, stateCode: TSFixMe): TSFixMe => {
    let param = name

    if (stateCode) {
      param = `${param} ${getStateNameByCountryAndStateCode(values.countryCode, stateCode)}`
    }

    return kebabCase(param)
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <Card.Content noHeader>
        <Row>
          <Column width="50%">
            <Select
              id="countryCode"
              value={values.countryCode}
              onChange={handleCountryChange}
              onBlur={handleBlur}
              touched={touched.countryCode}
              errors={errors.countryCode}
              label={<FormattedMessage id="dictionary.countryCode" defaultMessage="Country" />}
              disabled={isEdit}
            >
              <option value="">{intl.formatMessage(messages.selectCountryCode)}</option>

              {COUNTRIES.map((country): TSFixMe => {
                return (
                  <option key={country.code} value={country.code}>
                    {`${country.label} - ${country.code}`}
                  </option>
                )
              })}
            </Select>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="name"
              onChange={handleNameChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="dictionary.name" defaultMessage="Name" />}
            />
          </Column>

          <Column width="50%">
            <Input
              id="param"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.param}
              placeholder={intl.formatMessage(messages.paramPlaceholder)}
              touched={touched.param}
              errors={errors.param}
              label={<FormattedMessage id="dictionary.param" defaultMessage="Param" />}
            />
          </Column>
        </Row>

        <Row>
          <Column width="50%">
            <Input
              id="code"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.code}
              placeholder={intl.formatMessage(messages.codePlaceholder)}
              touched={touched.code}
              errors={errors.code}
              label={<FormattedMessage id="dictionary.code" defaultMessage="Code" />}
              readOnly={isEdit}
            />
          </Column>
        </Row>

        <Row>
          <Column width="50%">
            <SelectState
              id="stateCode"
              value={values.stateCode}
              onChange={handleStateChange}
              onBlur={handleBlur}
              touched={touched.stateCode}
              errors={errors.stateCode}
              label={<FormattedMessage id="dictionary.state" defaultMessage="State" />}
              country={values.countryCode}
            />
          </Column>
          <Column width="50%">
            <SelectTimezone
              id="timezone"
              value={values.timezone}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.timezone}
              errors={errors.timezone}
              label={<FormattedMessage id="dictionary.timezone" defaultMessage="Timezone" />}
              country={values.countryCode}
              disabled={isEdit}
            />
          </Column>
        </Row>

        <Row>
          <Column>
            <InputTags
              id="tags"
              values={values.tags}
              onChange={handleTagsChange}
              onBlur={handleBlur}
              placeholder={intl.formatMessage(messages.tagsPlaceholder)}
              label={<FormattedMessage id="cities.new.form.tags.label" defaultMessage="Tags (Optional)" />}
            />
          </Column>
        </Row>
      </Card.Content>

      <Card.Footer>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage id="cities.new.form.error" defaultMessage="There was an error while saving your city" />
            </ErrorMessage>
          </div>
        )}

        <Card.FooterActions>
          <Button as={NavLink} to="/cities" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>

          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </Card.FooterActions>
      </Card.Footer>
    </form>
  )
}

export const NewCity = ({ city, isEdit, onSubmit, error, loading }: TSFixMe): TSFixMe => {
  const intl = useIntl()

  const initialCity = {
    countryCode: city.countryCode || '',
    name: city.name || '',
    param: city.param || '',
    stateCode: city.stateCode || '',
    tags: city.tags || [],
    timezone: city.timezone || '',
    code: city.code || ''
  }

  return (
    <Formik initialValues={initialCity} validationSchema={newCitySchema(intl)} onSubmit={onSubmit}>
      {(props): TSFixMe => {
        return <NewCityForm {...props} error={error} loading={loading} isEdit={isEdit} />
      }}
    </Formik>
  )
}
